import { useCallback, useEffect, useRef, useState } from 'react';
import { StatusBadge, ConfirmDialog, SettingsSection } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { adminApi, type MohSoundItem } from '@/shared/api/adminApi';
import { apiClient } from '@/shared/api/client';
import { toast } from '@/components/ui/Toast';

const MAX_BYTES = 4 * 1024 * 1024; // 4MB, matching the server validation.
const ACCEPT = 'audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/ogg,.mp3,.wav,.ogg';

/**
 * ספריית מוזיקת ההמתנה (MOH): מעלים כמה קבצים, מסמנים אחד כברירת-מחדל, וכל
 * תור יכול לבחור צליל משלו במסך התורים. הקבצים נשמרים ב-GCS ושורדים deploy.
 */
export function MohLibrarySection() {
  const [sounds, setSounds] = useState<MohSoundItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState<MohSoundItem | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.mohSounds().then(setSounds).catch(() => toast('שגיאה בטעינת הצלילים', 'danger')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function upload(file: File) {
    if (!file.type.startsWith('audio/') && !/\.(mp3|wav|ogg)$/i.test(file.name)) {
      toast('יש להעלות קובץ אודיו (mp3 / wav / ogg)', 'danger'); return;
    }
    if (file.size > MAX_BYTES) { toast('הקובץ גדול מדי — עד 4MB', 'danger'); return; }
    setBusy(true);
    try {
      await adminApi.uploadMohSound(file);
      toast('הצליל הועלה', 'ok');
      load();
    } catch (e: unknown) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'העלאה נכשלה', 'danger');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function makeDefault(s: MohSoundItem) {
    setBusy(true);
    try { await adminApi.setMohSoundDefault(s.id); load(); }
    catch { toast('שגיאה', 'danger'); }
    finally { setBusy(false); }
  }

  async function del(s: MohSoundItem) {
    try { await adminApi.deleteMohSound(s.id); toast('נמחק', 'ok'); setConfirmDel(null); load(); }
    catch { toast('שגיאה במחיקה', 'danger'); }
  }

  return (
    <SettingsSection
      title="ספריית מוזיקת המתנה"
      icon="headphones"
      selfSaving
      badge={sounds.length > 0 ? { label: sounds.length, tone: 'neutral' } : undefined}
      description={<>העלה קבצי מוזיקה שהלקוח ישמע בהמתנה בתור. סמן אחד כברירת-מחדל, ובמסך <strong>תורים</strong> אפשר לבחור לכל תור צליל משלו. mp3/wav/ogg, עד 4MB. הקבצים נשמרים בענן ושורדים עדכוני מערכת.</>}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
      />

      {loading ? (
        <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>טוען…</div>
      ) : sounds.length === 0 ? (
        <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', padding: '8px 0' }}>אין עדיין צלילים בספרייה.</div>
      ) : (
        <div className="stack" style={{ gap: 6 }}>
          {sounds.map((s) => <MohRow key={s.id} sound={s} busy={busy} onDefault={() => makeDefault(s)} onDelete={() => setConfirmDel(s)} />)}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button type="button" className="btn primary" onClick={() => inputRef.current?.click()} disabled={busy}>
          <Icon name="upload" size={14} /> {busy ? 'מעלה…' : 'העלה צליל'}
        </button>
      </div>

      <ConfirmDialog
        isOpen={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={() => { if (confirmDel) void del(confirmDel); }}
        title="מחיקת צליל"
        message={`למחוק את "${confirmDel?.name}"? תורים שהשתמשו בו יחזרו לברירת המחדל.`}
        confirmLabel="מחק"
        tone="danger"
      />
    </SettingsSection>
  );
}

function MohRow({ sound, busy, onDefault, onDelete }: { sound: MohSoundItem; busy: boolean; onDefault: () => void; onDelete: () => void }) {
  // ה-preview דורש Bearer, אז מושכים blob ומנגנים דרך object URL.
  const [url, setUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  async function loadPreview() {
    if (url) return;
    setLoadingPreview(true);
    try {
      const res = await apiClient.get(`/admin/moh-sounds/${sound.id}/preview`, { responseType: 'blob' });
      setUrl(URL.createObjectURL(res.data as Blob));
    } catch {
      toast('לא ניתן לנגן את הצליל', 'danger');
    } finally { setLoadingPreview(false); }
  }
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-2)', flexWrap: 'wrap' }}>
      <Icon name="mic" size={15} color="var(--muted)" />
      <div style={{ flex: 1, minWidth: 120 }}>
        <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>{sound.name}</div>
        {sound.filename && <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }} dir="ltr">{sound.filename}</div>}
      </div>
      {sound.is_default && <StatusBadge tone="ok" size="sm">ברירת מחדל</StatusBadge>}

      {url ? (
        <audio src={url} controls style={{ height: 32, maxWidth: 180 }} />
      ) : (
        <button type="button" className="btn ghost" onClick={() => void loadPreview()} disabled={loadingPreview} title="נגן">
          <Icon name="play" size={14} /> {loadingPreview ? '…' : 'נגן'}
        </button>
      )}

      {!sound.is_default && (
        <button type="button" className="btn ghost" onClick={onDefault} disabled={busy} title="קבע כברירת מחדל">
          <Icon name="star" size={14} /> ברירת מחדל
        </button>
      )}
      <button type="button" className="btn ghost" onClick={onDelete} aria-label="מחק" title="מחק" style={{ color: 'var(--danger)' }}>
        <Icon name="trash" size={14} />
      </button>
    </div>
  );
}
